export class CameraBall extends Phaser.Geom.Point {
  protected scene: Phaser.Scene;
  protected camera: Phaser.Cameras.Scene2D.Camera;

  protected isMoving: boolean;
  protected isPanningByTime: boolean;
  protected isPanningBySpeed: boolean;
  protected hasTargetSpeed: boolean;

  protected baseAccelerationXPerMS: number;
  protected baseDecelerationXPerMS: number;
  protected baseSpeedXPerMS: number;
  protected panSpeedXPerMS: number;
  protected speedXPerMS: number;
  protected targetAccelerationXPerMS: number;
  protected targetDecelerationXPerMS: number;
  protected targetSpeedXPerMS: number;

  protected panDuration: number;
  protected panElapsedTime: number;
  protected panStartX: number;
  protected panOffsetX: number;
  protected prevPanOffsetX: number;
  protected panMovement: number;
  protected panTargetX: number;
  protected panEase: ((progress: number, ...rest: any) => number) | undefined;

  constructor(scene: Phaser.Scene, camera: Phaser.Cameras.Scene2D.Camera) {
    super();

    this.scene = scene;
    this.camera = camera;

    this.isMoving = false;
    this.isPanningByTime = false;
    this.isPanningBySpeed = false;
    this.hasTargetSpeed = false;

    this.baseAccelerationXPerMS = 0;
    this.baseDecelerationXPerMS = 0;
    this.baseSpeedXPerMS = 0.625;
    this.panSpeedXPerMS = 0.3125;
    this.speedXPerMS = this.baseSpeedXPerMS;
    this.targetAccelerationXPerMS = 0;
    this.targetDecelerationXPerMS = 0;
    this.targetSpeedXPerMS = 0;

    this.panDuration = 0;
    this.panElapsedTime = 0;
    this.panStartX = 0;
    this.panOffsetX = 0;
    this.prevPanOffsetX = 0;
    this.panMovement = 0;
    this.panTargetX = 0;

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  get Camera(): Phaser.Cameras.Scene2D.Camera {
    return this.camera;
  }

  get IsPanning(): boolean {
    return this.isPanningBySpeed;
  }

  public toggleMoving(shouldMove: boolean) {
    this.isMoving = shouldMove;
  }

  public setSpeedX(speed: number) {
    this.speedXPerMS = speed;
    this.targetSpeedXPerMS = speed;
  }

  public setTargetSpeedX(speed: number, accelerationXPerMS: number = this.baseAccelerationXPerMS, decelerationXPerMS: number = this.baseDecelerationXPerMS) {
    this.targetSpeedXPerMS = speed;
    this.targetAccelerationXPerMS = accelerationXPerMS;
    this.targetDecelerationXPerMS = decelerationXPerMS;

    this.hasTargetSpeed = true;
  }

  public panXToRelativePoint(targetX: number) {
    this.panOffsetX = 0;
    this.prevPanOffsetX = 0;
    this.panTargetX = targetX;

    this.isPanningBySpeed = true;
  }

  public panByTimeToXRelativePointX(relativeStartX: number, targetX: number, duration: number, ease: (progress: number, ...rest: any) => number) {
    this.panDuration = duration;
    this.panElapsedTime = 0;
    this.panEase = ease;

    this.panStartX = this.x - relativeStartX;
    this.panOffsetX = this.panStartX;
    this.prevPanOffsetX = 0;
    this.panTargetX = targetX;

    this.isPanningByTime = true;
  }

  public update(time: number, delta: number) {
    if (this.isPanningByTime) this.panByTime(time, delta);
    else if (this.isPanningBySpeed) this.panBySpeed(time, delta);

    if (this.isMoving) {
      if (this.hasTargetSpeed) {
        let diff = this.speedXPerMS - this.targetSpeedXPerMS;
        let acceleration = diff < 0 ? this.targetAccelerationXPerMS : this.targetDecelerationXPerMS;

        if (Math.abs(diff) > acceleration) {
          let progress = acceleration / diff;
          this.speedXPerMS = Phaser.Math.Interpolation.Linear([this.speedXPerMS, this.targetSpeedXPerMS], progress);
        } else {
          this.speedXPerMS = this.targetSpeedXPerMS;
          this.hasTargetSpeed = false;
        }

        // console.log("speed", this.speedXPerMS, this.targetSpeedXPerMS);
      }

      this.x = this.x + this.panMovement + this.speedXPerMS * delta;

      this.panMovement = 0;
    }

    // if (!Phaser.Math.Within(this.speedXPerMS, this.targetSpeedXPerMS, 0.0001)) {
    //   this.hasTargetSpeed = false;
    // }
  }

  protected panByTime(time: number, delta: number) {
    this.panElapsedTime += delta;

    let progress = Phaser.Math.Clamp(this.panElapsedTime / this.panDuration, 0, 1);
    progress = this?.panEase?.(progress) ?? progress;

    if (this.panElapsedTime < this.panDuration) {
      this.panOffsetX = (this.panTargetX - this.panStartX) * progress;
      this.panMovement = this.panOffsetX - this.prevPanOffsetX;
      this.prevPanOffsetX = this.panOffsetX;
    } else {
      this.panOffsetX = this.panTargetX - this.panStartX;
      this.panMovement = this.panOffsetX - this.prevPanOffsetX;
      this.prevPanOffsetX = this.panOffsetX;

      this.isPanningByTime = false;
    }
  }

  protected panBySpeed(time: number, delta: number) {
    let panSpeedX = this.panSpeedXPerMS * delta;
    let distanceToTarget = this.panTargetX - this.panOffsetX;
    let distanceToTargetAbs = Math.abs(distanceToTarget);

    if (distanceToTargetAbs > panSpeedX) {
      let progress = panSpeedX / distanceToTargetAbs;

      this.panOffsetX = Phaser.Math.Interpolation.Bezier([this.panOffsetX, this.panTargetX], progress);
      this.panMovement = this.panOffsetX - this.prevPanOffsetX;
      this.prevPanOffsetX = this.panOffsetX;
    } else {
      this.panOffsetX = this.panTargetX;
      this.panMovement = this.panOffsetX - this.prevPanOffsetX;
      this.prevPanOffsetX = this.panOffsetX;

      this.isPanningBySpeed = false;
    }
  }

  protected shutdown() {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
  }
}
